import { useState } from 'react';
import { 
  Box, 
  Button, 
  Typography, 
  List, 
  ListItem, 
  ListItemText, 
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Chip,
  Tooltip,
  Divider
} from '@mui/material';
import { 
  Add as AddIcon, 
  Send as SendIcon, 
  Visibility as VisibilityIcon,
  Link as LinkIcon
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { documentsApi, dealsApi, contactsApi } from '../api';
import type { DocumentTemplate, GeneratedProposal, Contact } from '../types/api';

interface DocumentsPanelProps {
  dealId: string;
}

export default function DocumentsPanel({ dealId }: DocumentsPanelProps) {
  const [openTemplateModal, setOpenTemplateModal] = useState(false);
  const [openSendModal, setOpenSendModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [selectedProposal, setSelectedProposal] = useState<string>('');
  const [selectedContact, setSelectedContact] = useState<string>('');
  const queryClient = useQueryClient();

  // Fetch templates
  const { data: templates, isLoading: isLoadingTemplates } = useQuery<DocumentTemplate[]>(
    ['templates'],
    async () => {
      return await documentsApi.getTemplates();
    }
  );

  // Fetch proposals for this deal
  const { data: proposals, isLoading: isLoadingProposals } = useQuery<GeneratedProposal[]>(
    ['proposals', dealId],
    async () => {
      return await documentsApi.getProposalsByDeal(dealId);
    }
  );

  // Fetch contacts for this deal's account
  const { data: contacts, isLoading: isLoadingContacts } = useQuery<Contact[]>(
    ['contacts', dealId],
    async () => {
      const deal = await dealsApi.getDeal(dealId);
      const accountId = deal.account_id;
      return await contactsApi.getContactsByAccount(accountId);
    }
  );

  // Mutation to create a new proposal
  const createProposalMutation = useMutation(
    async () => {
      return await documentsApi.createProposal({
        deal_id: dealId,
        template_id: selectedTemplate
      });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['proposals', dealId]);
        setOpenTemplateModal(false);
        setSelectedTemplate('');
      }
    }
  );

  // Mutation to send a proposal
  const sendProposalMutation = useMutation(
    async () => {
      return await documentsApi.sendProposal({
        proposal_id: selectedProposal,
        contact_id: selectedContact
      });
    },
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(['proposals', dealId]);
        setOpenSendModal(false);
        setSelectedProposal('');
        setSelectedContact('');
        
        // Copy the share link to clipboard
        const shareLink = `${window.location.origin}/portal/proposals/${data.data.share_token}`;
        navigator.clipboard.writeText(shareLink);
        alert('Proposal sent! Share link copied to clipboard.');
      }
    }
  );

  const handleCreateProposal = () => {
    createProposalMutation.mutate();
  };

  const handleSendProposal = () => {
    sendProposalMutation.mutate();
  };

  const getStatusChip = (status: string) => {
    let color: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' = 'default';
    
    switch (status) {
      case 'Draft':
        color = 'default';
        break;
      case 'Sent':
        color = 'primary';
        break;
      case 'Viewed':
        color = 'info';
        break;
      case 'Accepted':
        color = 'success';
        break;
      default:
        color = 'default';
    }
    
    return <Chip label={status} color={color} size="small" />;
  };

  const copyShareLink = (shareToken: string) => {
    const shareLink = `${window.location.origin}/portal/proposals/${shareToken}`;
    navigator.clipboard.writeText(shareLink);
    alert('Share link copied to clipboard!');
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Documents</Typography>
        <Button 
          variant="contained" 
          startIcon={<AddIcon />}
          onClick={() => setOpenTemplateModal(true)}
        >
          Generate Proposal
        </Button>
      </Box>

      {isLoadingProposals ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress size={24} />
        </Box>
      ) : proposals && proposals.length > 0 ? (
        <List>
          {proposals.map((proposal) => (
            <ListItem key={proposal.id} divider>
              <ListItemText
                primary={`Proposal ${proposal.id.substring(0, 8)}...`}
                secondary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                    {getStatusChip(proposal.status)}
                    <Typography variant="caption">
                      {new Date(proposal.created_at).toLocaleDateString()}
                    </Typography>
                  </Box>
                }
              />
              <ListItemSecondaryAction>
                <Tooltip title="View Proposal">
                  <IconButton 
                    edge="end" 
                    aria-label="view"
                    onClick={() => window.open(`/portal/proposals/${proposal.share_token}`, '_blank')}
                  >
                    <VisibilityIcon />
                  </IconButton>
                </Tooltip>
                {proposal.status === 'Draft' && (
                  <Tooltip title="Send Proposal">
                    <IconButton 
                      edge="end" 
                      aria-label="send"
                      onClick={() => {
                        setSelectedProposal(proposal.id);
                        setOpenSendModal(true);
                      }}
                      sx={{ ml: 1 }}
                    >
                      <SendIcon />
                    </IconButton>
                  </Tooltip>
                )}
                {proposal.status !== 'Draft' && (
                  <Tooltip title="Copy Share Link">
                    <IconButton 
                      edge="end" 
                      aria-label="link"
                      onClick={() => copyShareLink(proposal.share_token)}
                      sx={{ ml: 1 }}
                    >
                      <LinkIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
          No proposals generated yet. Click "Generate Proposal" to create one.
        </Typography>
      )}

      {/* Template Selection Modal */}
      <Dialog open={openTemplateModal} onClose={() => setOpenTemplateModal(false)}>
        <DialogTitle>Generate New Proposal</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Select a template to generate a proposal for this deal.
          </Typography>
          <FormControl fullWidth>
            <InputLabel id="template-select-label">Template</InputLabel>
            <Select
              labelId="template-select-label"
              id="template-select"
              value={selectedTemplate}
              label="Template"
              onChange={(e) => setSelectedTemplate(e.target.value as string)}
            >
              {isLoadingTemplates ? (
                <MenuItem disabled>Loading templates...</MenuItem>
              ) : templates && templates.length > 0 ? (
                templates.map((template) => (
                  <MenuItem key={template.id} value={template.id}>
                    {template.name}
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled>No templates available</MenuItem>
              )}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenTemplateModal(false)}>Cancel</Button>
          <Button 
            onClick={handleCreateProposal} 
            disabled={!selectedTemplate || createProposalMutation.isLoading}
            variant="contained"
          >
            {createProposalMutation.isLoading ? <CircularProgress size={24} /> : 'Generate'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Send Proposal Modal */}
      <Dialog open={openSendModal} onClose={() => setOpenSendModal(false)}>
        <DialogTitle>Send Proposal</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Select a contact to send this proposal to.
          </Typography>
          <FormControl fullWidth>
            <InputLabel id="contact-select-label">Contact</InputLabel>
            <Select
              labelId="contact-select-label"
              id="contact-select"
              value={selectedContact}
              label="Contact"
              onChange={(e) => setSelectedContact(e.target.value as string)}
            >
              {isLoadingContacts ? (
                <MenuItem disabled>Loading contacts...</MenuItem>
              ) : contacts && contacts.length > 0 ? (
                contacts.map((contact) => (
                  <MenuItem key={contact.id} value={contact.id}>
                    {contact.first_name} {contact.last_name} ({contact.email})
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled>No contacts available</MenuItem>
              )}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenSendModal(false)}>Cancel</Button>
          <Button 
            onClick={handleSendProposal} 
            disabled={!selectedContact || sendProposalMutation.isLoading}
            variant="contained"
            startIcon={<SendIcon />}
          >
            {sendProposalMutation.isLoading ? <CircularProgress size={24} /> : 'Send'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
} 